import React from 'react';
import { Button, Collapse, DotLoading, Empty, List, NavBar } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { profilePointsPageStyles } from './styles';
import { DailyPointSummary, PointEventType, ProfilePointsState } from './types';

interface ProfilePointsViewProps {
  state: ProfilePointsState;
  onRefresh: () => Promise<void>;
}

export const ProfilePointsView: React.FC<ProfilePointsViewProps> = ({ state, onRefresh }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['profile', 'common']);

  const getEventLabel = (eventType: PointEventType): string => {
    const key = `points.events.${eventType}`;
    const translated = t(key);
    return translated === key ? eventType : translated;
  };

  const formatDateLabel = (date: string) => {
    const today = getDhakaDateOffset(0);
    const yesterday = getDhakaDateOffset(-1);
    const beforeYesterday = getDhakaDateOffset(-2);
    if (date === today) {
      return t('points.today');
    }
    if (date === yesterday) {
      return t('points.yesterday');
    }
    if (date === beforeYesterday) {
      return t('points.beforeYesterday');
    }
    return date;
  };

  return (
    <div className="webview-page" style={profilePointsPageStyles.page}>
      <NavBar
        onBack={() => navigate(-1)}
        right={
          <Button size="mini" onClick={() => void onRefresh()} loading={state.loading}>
            {t('common:refresh')}
          </Button>
        }
      >
        {t('points.navTitle')}
      </NavBar>

      <div style={profilePointsPageStyles.heroCard}>
        <div style={profilePointsPageStyles.heroLabel}>{t('points.totalPoints')}</div>
        <div style={profilePointsPageStyles.heroValue}>{state.totalPoints.toLocaleString()}</div>
        <div style={profilePointsPageStyles.heroHint}>{t('points.timezoneHint')}</div>
      </div>

      {state.loading && state.items.length === 0 ? (
        <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DotLoading />
        </div>
      ) : state.items.length === 0 ? (
        <Empty description={t('points.empty')} />
      ) : (
        <div style={profilePointsPageStyles.listWrap}>
          <List>
            {state.items.map((item) => (
              <List.Item key={item.date}>
                <DailyPointRow item={item} dateLabel={formatDateLabel(item.date)} getEventLabel={getEventLabel} />
              </List.Item>
            ))}
          </List>
        </div>
      )}
    </div>
  );
};

interface DailyPointRowProps {
  item: DailyPointSummary;
  dateLabel: string;
  getEventLabel: (eventType: PointEventType) => string;
}

const DailyPointRow: React.FC<DailyPointRowProps> = ({ item, dateLabel, getEventLabel }) => {
  const { t } = useTranslation('profile');
  const detail = (
    <div style={profilePointsPageStyles.sourceList}>
      {item.sources.map((source) => (
        <div key={source.event_type} style={profilePointsPageStyles.sourceRow}>
          <span>
            {getEventLabel(source.event_type)} · {t('points.recordCount', { count: source.record_count })}
          </span>
          <span style={profilePointsPageStyles.sourcePoints}>+{source.points}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Collapse accordion>
      <Collapse.Panel
        key={item.date}
        title={
          <div style={profilePointsPageStyles.rowMain}>
            <div>
              <div style={profilePointsPageStyles.dateText}>{dateLabel}</div>
              <div style={profilePointsPageStyles.countText}>
                {item.date} · {t('points.recordCount', { count: item.record_count })}
              </div>
            </div>
            <div style={profilePointsPageStyles.pointText}>+{item.points}</div>
          </div>
        }
      >
        {item.sources.length > 0 ? detail : <div style={profilePointsPageStyles.countText}>{t('points.noSources')}</div>}
      </Collapse.Panel>
    </Collapse>
  );
};

function getDhakaDateOffset(offsetDays: number): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Dhaka',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return formatter.format(date);
}
